import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles, Shield, Eye, Briefcase, Heart, Wallet, Compass, AlertTriangle,
  Share2, Download, Copy, MessageCircle, Instagram, ArrowRight, X, Check, Brain, Lock,
} from "lucide-react";
import { toast } from "sonner";
import { api, track, formatErr } from "@/lib/api";
import { loginWithGoogle } from "@/lib/auth";
import { CharacterVisual } from "@/components/CharacterVisual";
import CardCanvas from "@/components/CardCanvas";

const CAT_ICON = { kerja: Briefcase, uang: Wallet, relasi: Heart, diri: Brain, keputusan: Compass };
const CAT_COLOR = { kerja: "#3B82F6", uang: "#10B981", relasi: "#EC4899", diri: "#8B5CF6", keputusan: "#E5C158" };
const CAT_LABEL = { kerja: "Kerja", uang: "Uang", relasi: "Relasi", diri: "Diri Sendiri", keputusan: "Keputusan" };

function Counter({ target }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    let raf; const start = performance.now(); const dur = 1400;
    const tick = (t) => {
      const p = Math.min((t - start) / dur, 1);
      setN(Math.round(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target]);
  return <span>{n}</span>;
}

const Section = ({ icon: Icon, title, children, delay = 0, color = "#E5C158" }) => (
  <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
    transition={{ duration: 0.5, delay }} className="card-surface rounded-2xl p-6 sm:p-7">
    <div className="flex items-center gap-3 mb-4">
      <div className="w-9 h-9 rounded-lg flex items-center justify-center border"
        style={{ background: `${color}18`, borderColor: `${color}40` }}>
        <Icon size={18} style={{ color }} />
      </div>
      <h3 className="text-xs font-semibold tracking-[0.2em] uppercase text-amber-300/80">{title}</h3>
    </div>
    {children}
  </motion.div>
);

const Bullets = ({ items }) => (
  <ul className="space-y-2.5">
    {(items || []).map((it, i) => (
      <li key={`${i}-${it}`} className="flex gap-3 text-sm sm:text-base text-gray-300 leading-relaxed">
        <span className="text-amber-400 mt-1.5 shrink-0">•</span><span>{it}</span>
      </li>
    ))}
  </ul>
);

export default function Result() {
  const { publicId } = useParams();
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [reveal, setReveal] = useState(true);
  const [imageData, setImageData] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const cardRef = useRef(null);
  const pollRef = useRef(null);
  const [rareBurst, setRareBurst] = useState(false);
  const rarePlayed = useRef(false);
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    api.get("/user/today-status").then((r) => setProgress(r.data)).catch(() => {});
  }, []);

  const [payBusy, setPayBusy] = useState(false);
  const [waitingPay, setWaitingPay] = useState(false);

  const refetchResult = useCallback(async () => {
    const { data: fresh } = await api.get(`/results/${publicId}`);
    setResult(fresh);
  }, [publicId]);

  const checkPayment = useCallback(async (silent = false) => {
    try {
      const { data } = await api.get("/purchases/status");
      if (data.paid) {
        setWaitingPay(false);
        await refetchResult();
        toast.success("Pembayaran sukses! Hasil lengkap lo kebuka 🎉");
        track("payment_unlocked", null, { public_id: publicId });
        return true;
      }
      if (!silent) toast("Pembayaran belum terkonfirmasi. Kalau baru aja bayar, tunggu beberapa detik ya.");
      return false;
    } catch (e) {
      if (!silent) toast.error("Gagal cek status. Coba lagi ya.");
      return false;
    }
  }, [publicId, refetchResult]);

  useEffect(() => {
    if (!result?.locked) return undefined;
    const iv = setInterval(async () => {
      try {
        const { data } = await api.get("/purchases/status");
        if (data.paid) {
          clearInterval(iv);
          setWaitingPay(false);
          await refetchResult();
          toast.success("Pembayaran sukses! Hasil lengkap lo kebuka 🎉");
          track("payment_unlocked", null, { public_id: publicId });
        }
      } catch (e) { clearInterval(iv); }
    }, 4000);
    return () => clearInterval(iv);
  }, [result?.locked, publicId, refetchResult]);

  const buyFullAccess = async () => {
    setPayBusy(true);
    track("payment_started", null, { public_id: publicId });
    try {
      const { data } = await api.post("/purchases");
      if (data.status === "paid") { await refetchResult(); return; }
      const win = window.open(data.payment_url, "_blank");
      if (win) {
        try { win.opener = null; } catch (e) { /* noop */ }
        setWaitingPay(true);
      } else {
        toast.error("Popup diblokir browser. Izinkan popup, atau klik tombol lagi ya.");
        window.open(data.payment_url, "_blank");
      }
    } catch (e) {
      if (e.response?.status === 401) { loginWithGoogle(); return; }
      toast.error(formatErr(e.response?.data?.detail));
    } finally { setPayBusy(false); }
  };

  const shareUrl = `${process.env.REACT_APP_BACKEND_URL}/api/share/${publicId}`;

  const fetchImage = useCallback(async (path) => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      const reader = new FileReader();
      reader.onloadend = () => setImageData(reader.result);
      reader.readAsDataURL(res.data);
    } catch (e) { console.error("fetchImage failed", e); }
  }, []);

  useEffect(() => {
    api.get(`/results/${publicId}`).then(({ data }) => {
      setResult(data);
      if (data.character_image_url) fetchImage(data.character_image_url);
      else if (data.image_status === "pending") {
        pollRef.current = setInterval(async () => {
          try {
            const { data: st } = await api.get(`/results/${publicId}/status`);
            if (st.image_status === "done" && st.character_image_url) {
              clearInterval(pollRef.current);
              setResult((r) => ({ ...r, character_image_url: st.character_image_url, image_status: "done" }));
              fetchImage(st.character_image_url);
              track("character_generated", null, { public_id: publicId });
            } else if (st.image_status === "failed") {
              clearInterval(pollRef.current);
              setResult((r) => ({ ...r, image_status: "failed" }));
            }
          } catch (e) { /* ignore */ }
        }, 3000);
      }
    }).catch(() => setNotFound(true));
    const t = setTimeout(() => setReveal(false), 2400);
    return () => { clearTimeout(t); clearInterval(pollRef.current); };
  }, [publicId, fetchImage]);

  const captureBlob = async () => {
    const html2canvas = (await import("html2canvas")).default;
    const canvas = await html2canvas(cardRef.current, {
      useCORS: true, backgroundColor: "#0B0F19", scale: 1, width: 1080, height: 1920,
      windowWidth: 1080, windowHeight: 1920,
    });
    return new Promise((res) => canvas.toBlob(res, "image/png", 0.95));
  };

  const downloadCard = async (evtName = "image_downloaded") => {
    try {
      setDownloading(true);
      const blob = await captureBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `ruang-kendali-${result.character.key}.png`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      track(evtName, null, { public_id: publicId });
      api.post(`/results/${publicId}/share`);
      toast.success("Kartu berhasil di-download 🎉");
    } catch (e) {
      toast.error("Gagal bikin kartu. Coba lagi ya.");
    } finally { setDownloading(false); }
  };

  const nativeShare = async () => {
    try {
      const blob = await captureBlob();
      const file = new File([blob], `ruang-kendali-${result.character.key}.png`, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "Ruang Kendali", text: `Karakter gue: ${result.character.name}. Cek punya lo!`, url: shareUrl });
        track("share_clicked", null, { via: "native" });
        api.post(`/results/${publicId}/share`);
      } else {
        downloadCard();
      }
    } catch (e) { /* user cancelled */ }
  };

  const shareWhatsApp = () => {
    const text = encodeURIComponent(`Karakter Ruang Kendali gue ternyata "${result.character.name}" 👀 Penasaran karakter lo apa? ${shareUrl}`);
    window.open(`https://wa.me/?text=${text}`, "_blank");
    track("whatsapp_clicked", null, { public_id: publicId });
    api.post(`/results/${publicId}/share`);
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast.success("Link disalin ✓"); }
    catch { toast(shareUrl); }
    track("copy_link_clicked", null, { public_id: publicId });
  };

  const openShare = () => { setShareOpen(true); track("share_clicked", null, { public_id: publicId }); };

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 text-center">
        <div>
          <p className="font-display text-4xl gold-text mb-4">Hasil nggak ketemu</p>
          <button onClick={() => navigate("/")} className="gold-btn rounded-full px-6 py-3">Balik ke beranda</button>
        </div>
      </div>
    );
  }
  if (!result) {
    return <div className="min-h-screen flex items-center justify-center"><div className="w-14 h-14 rounded-full border-2 border-amber-400/30 border-t-amber-300 animate-spin" /></div>;
  }

  const a = result.analysis || {};
  const ch = result.character;
  const isRare = ch.rarity === "rare";
  const greeting = a.greeting || (result.user_name ? `Halo, ${result.user_name.split(" ")[0]}.` : "Halo.");

  if (isRare && !reveal && !rarePlayed.current) {
    rarePlayed.current = true;
    setTimeout(() => {
      setRareBurst(true);
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        const ac = new Ctx();
        [523.25, 659.25, 783.99, 1046.5].forEach((f, i) => {
          const o = ac.createOscillator(); const g = ac.createGain();
          o.type = "sine"; o.frequency.value = f;
          o.connect(g); g.connect(ac.destination);
          const t = ac.currentTime + i * 0.12;
          g.gain.setValueAtTime(0.0001, t);
          g.gain.exponentialRampToValueAtTime(0.25, t + 0.03);
          g.gain.exponentialRampToValueAtTime(0.0001, t + 0.9);
          o.start(t); o.stop(t + 0.95);
        });
      } catch (e) { /* no audio */ }
      setTimeout(() => setRareBurst(false), 2600);
    }, 100);
  }

  return (
    <div className="relative min-h-screen w-full">
      {/* rare celebration */}
      <AnimatePresence>
        {rareBurst && (
          <motion.div className="fixed inset-0 z-40 pointer-events-none flex items-center justify-center overflow-hidden"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_40%,rgba(229,193,88,0.35),transparent_60%)]" />
            {[...Array(28)].map((_, i) => {
              const ang = (i / 28) * Math.PI * 2;
              return (
                <motion.span key={i}
                  className="absolute w-2 h-2 rounded-full"
                  style={{ background: i % 2 ? "#F0D97A" : "#E5C158", top: "42%", left: "50%" }}
                  initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
                  animate={{ x: Math.cos(ang) * 320, y: Math.sin(ang) * 320, opacity: 0, scale: 0.4 }}
                  transition={{ duration: 1.6, ease: "easeOut" }}
                />
              );
            })}
            <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", damping: 12 }}
              className="relative text-center">
              <div className="text-6xl mb-2">{ch.icon}</div>
              <div className="font-display text-3xl sm:text-4xl gold-text">KARAKTER LANGKA!</div>
              <div className="text-sm text-amber-200/80 mt-1 tracking-[0.2em] uppercase">Cuma segelintir orang dapet ini</div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* reveal overlay */}
      <AnimatePresence>
        {reveal && (
          <motion.div className="fixed inset-0 z-50 rk-bg flex items-center justify-center px-8 text-center"
            initial={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.7 }}>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
              className="font-display text-3xl sm:text-5xl leading-tight">
              RUANG KENDALI SELESAI<br /><span className="gold-text">MEMBACA POLA LO.</span>
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="relative z-10 max-w-2xl mx-auto px-5 sm:px-8 pt-10 pb-28">
        {/* character hero */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: reveal ? 0 : 1 }} transition={{ duration: 0.6, delay: 0.1 }}
          className="text-center">
          <p className="text-base text-amber-100/90 mb-2 font-display text-2xl" data-testid="result-greeting">{greeting}</p>
          <p className="text-xs font-semibold tracking-[0.3em] uppercase text-amber-400/80 mb-4">Karakter lo adalah…</p>
          <div className="rounded-3xl overflow-hidden border border-amber-400/20 aspect-[3/4] max-w-sm mx-auto mb-6">
            <CharacterVisual icon={ch.icon} name={ch.name} imageUrl={result.character_image_url}
              image={imageData} status={result.image_status} category={result.category} className="w-full h-full" />
          </div>
          <h1 className="font-display text-5xl sm:text-6xl font-bold gold-text leading-none" data-testid="character-name">{ch.name}</h1>
          <div className="mt-4 font-mono text-2xl text-amber-300" data-testid="compatibility-score">
            <Counter target={result.compatibility} />% KESESUAIAN
          </div>
          {ch.rarity === "rare" && (
            <div data-testid="rare-badge" className="mt-4 inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-amber-300/60 bg-amber-400/10 text-amber-200 text-xs font-semibold tracking-[0.2em] uppercase">
              <Sparkles size={13} /> Karakter Langka
            </div>
          )}
          <p className="mt-4 text-lg text-gray-300 italic">"{ch.tagline}"</p>

          <button onClick={openShare} data-testid="open-share-button"
            className="mt-8 gold-btn rounded-full px-8 py-4 inline-flex items-center gap-3">
            <Share2 size={18} /> SHARE KARAKTER GUE
          </button>
        </motion.div>

        {/* sections */}
        <div className="mt-14 space-y-4">
          {result.locked && (
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }} data-testid="paywall-section"
              className="relative glass rounded-3xl overflow-hidden">
              <div className="p-8 sm:p-10 blur-md select-none pointer-events-none" aria-hidden="true">
                <div className="h-3.5 w-1/3 rounded bg-white/15 mb-6" />
                <div className="space-y-3 mb-8">
                  <div className="h-3 w-full rounded bg-white/10" />
                  <div className="h-3 w-11/12 rounded bg-white/10" />
                  <div className="h-3 w-4/5 rounded bg-white/10" />
                </div>
                <div className="grid grid-cols-2 gap-4 mb-8">
                  <div className="h-24 rounded-xl bg-white/8" />
                  <div className="h-24 rounded-xl bg-white/8" />
                </div>
                <div className="space-y-3">
                  <div className="h-3 w-full rounded bg-white/10" />
                  <div className="h-3 w-3/4 rounded bg-white/10" />
                </div>
              </div>
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 bg-slate-950/55">
                <div className="w-12 h-12 rounded-full bg-amber-400/15 border border-amber-400/40 flex items-center justify-center mb-4">
                  <Lock size={20} className="text-amber-300" />
                </div>
                <p className="font-display text-2xl sm:text-3xl leading-snug">
                  Analisis lengkap lo <span className="gold-text">masih terkunci</span>
                </p>
                <p className="mt-3 text-sm text-gray-400 max-w-sm">
                  Kenapa lo dapet karakter ini, kekuatan, blind spot, pola tersembunyi, sampai
                  Ruang Kendali — sekali bayar, semua 5 kategori kebuka selamanya.
                </p>
                <button onClick={buyFullAccess} disabled={payBusy || waitingPay} data-testid="unlock-cta-button"
                  className="mt-6 gold-btn rounded-full px-8 py-4 inline-flex items-center gap-3 disabled:opacity-60">
                  {payBusy
                    ? <span className="w-4 h-4 border-2 border-slate-900/40 border-t-slate-900 rounded-full animate-spin" />
                    : <Lock size={17} />}
                  {waitingPay ? "Menunggu pembayaran…" : `Buka Hasil Lengkap — ${result.price_label || "Rp9.900"}`}
                </button>
                {waitingPay && (
                  <div className="mt-4 flex flex-col items-center gap-2" data-testid="waiting-payment">
                    <p className="text-xs text-amber-200/80 flex items-center gap-2">
                      <span className="w-3 h-3 border-2 border-amber-300/40 border-t-amber-300 rounded-full animate-spin" />
                      Checkout Lynk kebuka di tab baru. Selesaikan bayar di sana — halaman ini kebuka otomatis.
                    </p>
                    <button onClick={() => checkPayment(false)} data-testid="check-payment-button"
                      className="text-xs text-gray-400 underline underline-offset-2 hover:text-amber-200 transition-colors">
                      Cek Status Pembayaran
                    </button>
                  </div>
                )}
                <p className="mt-3 text-xs text-gray-500">Habis bayar, halaman ini kebuka otomatis. Nggak perlu refresh.</p>
              </div>
            </motion.div>
          )}
          {!result.locked && (<>
          <Section icon={Sparkles} title="Kenapa lo dapet karakter ini?" delay={0}>
            <p className="text-sm sm:text-base text-gray-300 leading-relaxed">{a.why_character}</p>
            <p className="mt-3 text-xs text-gray-500 italic">Ini bukan diagnosis — cuma cerminan dari pola jawaban lo.</p>
          </Section>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Section icon={Shield} title="Kekuatan lo" delay={0.05} color="#10B981">
              <Bullets items={a.strengths} />
            </Section>
            <Section icon={Eye} title="Blind spot lo" delay={0.1} color="#EF4444">
              <Bullets items={a.blind_spots} />
            </Section>
          </div>

          <Section icon={CAT_ICON[result.category] || Compass} title={a.category_label || "Pola lo"} delay={0.05} color={CAT_COLOR[result.category] || "#E5C158"}>
            <p className="text-sm sm:text-base text-gray-300 leading-relaxed">{a.category_insight}</p>
          </Section>
          <Section icon={AlertTriangle} title="Yang mungkin sering lo abaikan" delay={0.05} color="#F59E0B">
            <p className="text-sm sm:text-base text-gray-200 leading-relaxed">{a.ignored_pattern}</p>
          </Section>

          {/* ruang kendali closer */}
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.6 }} className="glass rounded-3xl p-8 text-center">
            <p className="text-xs font-semibold tracking-[0.25em] uppercase text-amber-400/80 mb-4">Ruang Kendali lo</p>
            <p className="font-display text-2xl sm:text-3xl leading-snug text-white">{a.ruang_kendali}</p>
          </motion.div>

          {/* dimension scores */}
          <Section icon={Sparkles} title="Peta dimensi lo" delay={0}>
            <div className="space-y-3">
              {result.dimension_scores.map((d) => (
                <div key={d.key}>
                  <div className="flex justify-between text-xs text-gray-400 mb-1">
                    <span>{d.label}</span><span className="font-mono text-amber-300/80">{d.value}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-white/8 overflow-hidden">
                    <motion.div className="h-full rounded-full bg-gradient-to-r from-amber-300 to-amber-500"
                      initial={{ width: 0 }} whileInView={{ width: `${d.value}%` }} viewport={{ once: true }}
                      transition={{ duration: 0.8 }} />
                  </div>
                </div>
              ))}
            </div>
          </Section>
          </>)}

          {progress && (() => {
            const doneCount = progress.done.length;
            const remaining = (progress.categories || []).filter((c) => !progress.done.includes(c));
            const nextCat = remaining[0];
            return (
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                className="glass rounded-3xl p-7 text-center" data-testid="next-side-cta">
                <p className="font-display text-2xl sm:text-3xl leading-snug">
                  Ini baru sisi <span className="gold-text">{CAT_LABEL[result.category]}</span> lo.
                </p>
                <p className="mt-2 text-gray-400 text-sm">
                  {remaining.length > 0
                    ? `Masih ada ${remaining.length} sisi lagi buat ngeliat gambaran utuh diri lo.`
                    : "Lo udah cek semua sisi hari ini. Gambaran diri lo makin utuh 🔥"}
                </p>
                <div className="mt-5 flex items-center justify-center gap-1.5" data-testid="progress-dots">
                  {(progress.categories || []).map((c) => (
                    <span key={c} className={`h-2 rounded-full transition-all ${progress.done.includes(c) ? "w-8 bg-amber-400" : "w-2 bg-white/15"}`} />
                  ))}
                </div>
                <p className="mt-2 text-xs font-mono text-amber-300/80">{doneCount}/5 sisi kecek hari ini</p>
                {nextCat && (
                  <button onClick={() => navigate(`/mulai/${nextCat}`)} data-testid="next-category-button"
                    className="mt-6 gold-btn rounded-full px-7 py-3.5 inline-flex items-center gap-2">
                    Cek sisi {CAT_LABEL[nextCat]} <ArrowRight size={17} />
                  </button>
                )}
              </motion.div>
            );
          })()}

          <div className="text-center pt-6">
            <button onClick={() => navigate("/pilih")} data-testid="try-again-button"
              className="inline-flex items-center gap-2 text-amber-200/80 hover:text-amber-100 transition-colors">
              Cek kategori lain <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Share modal */}
      <AnimatePresence>
        {shareOpen && (
          <motion.div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShareOpen(false)}>
            <motion.div onClick={(e) => e.stopPropagation()}
              initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
              transition={{ type: "spring", damping: 26 }}
              className="w-full max-w-md glass rounded-3xl p-6 max-h-[92vh] overflow-y-auto no-scrollbar">
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-display text-2xl gold-text">Share karakter lo</h3>
                <button onClick={() => setShareOpen(false)} data-testid="close-share-button" className="text-gray-400 hover:text-white"><X size={20} /></button>
              </div>

              {/* preview */}
              <div className="mx-auto mb-6 rounded-2xl overflow-hidden border border-amber-400/20"
                style={{ width: 270, height: 480 }}>
                <div style={{ width: 1080, height: 1920, transform: "scale(0.25)", transformOrigin: "top left" }}>
                  <CardCanvas ref={cardRef} result={result} image={imageData} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <button onClick={() => downloadCard("image_downloaded")} disabled={downloading} data-testid="download-card-button"
                  className="gold-btn rounded-xl py-3.5 flex items-center justify-center gap-2 col-span-2 disabled:opacity-60">
                  {downloading ? <span className="w-4 h-4 border-2 border-slate-900/40 border-t-slate-900 rounded-full animate-spin" /> : <Download size={17} />}
                  Download hasil (9:16)
                </button>
                <button onClick={shareWhatsApp} data-testid="whatsapp-button"
                  className="rounded-xl py-3.5 flex items-center justify-center gap-2 border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 transition-colors">
                  <MessageCircle size={17} /> WhatsApp
                </button>
                <button onClick={() => downloadCard("instagram_clicked")} data-testid="instagram-button"
                  className="rounded-xl py-3.5 flex items-center justify-center gap-2 border border-pink-500/40 bg-pink-500/10 text-pink-300 hover:bg-pink-500/20 transition-colors">
                  <Instagram size={17} /> Instagram
                </button>
                <button onClick={() => downloadCard("tiktok_clicked")} data-testid="tiktok-button"
                  className="rounded-xl py-3.5 flex items-center justify-center gap-2 border border-white/20 bg-white/5 text-gray-200 hover:bg-white/10 transition-colors">
                  <Check size={17} /> TikTok
                </button>
                <button onClick={copyLink} data-testid="copy-link-button"
                  className="rounded-xl py-3.5 flex items-center justify-center gap-2 border border-amber-400/30 bg-amber-400/10 text-amber-200 hover:bg-amber-400/20 transition-colors">
                  <Copy size={17} /> Copy link
                </button>
                <button onClick={nativeShare} data-testid="native-share-button"
                  className="rounded-xl py-3.5 flex items-center justify-center gap-2 border border-white/15 bg-white/5 text-gray-200 hover:bg-white/10 transition-colors col-span-2">
                  <Share2 size={17} /> Share lewat aplikasi lain
                </button>
              </div>
              <p className="mt-4 text-center text-xs text-gray-500">Kartu hasil cuma nampilin karakter — jawaban detail lo tetap privat.</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
